import Logger from '../fw/logger';


export default class GoogleAnalytics {

  #rmp;
  #gaContext = false;
  #fullMinuteWatchedInterval = null;
  #fullMinuteWatchedTicker = 0;
  #errorFn = null;
  #adErrorFn = null;
  #playerStartFn = null;
  #srcChangedFn = null;
  #adImpressionFn = null;
  #bufferStalledFn = null;
  #bufferNotStalledAnymoreFn = null;
  #enterFullscreenFn = null;
  #exitFullscreenFn = null;
  #gaEndedFn = null;
  #adClickFn = null;
  #readyFn = null;
  #seekingFn = null;
  #gaPauseFn = null;
  #adParserLoadErrorFn = null;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  #hasGtag() {
    if (typeof window.gtag === 'undefined') {
      this.#rmp.rmpUtils.warning(13000);
      return false;
    }
    return true;
  }

  #ready() {
    if (this.#hasGtag()) {
      window.gtag('event', 'ready', this.#rmp.gaEventParameters);
      Logger.print('google-analytics', 'ready event sent');
      if (this.#gaContext) {
        const streamType = this.#rmp.streamType;
        window.gtag('event', streamType, this.#rmp.gaEventParameters);
        Logger.print('google-analytics', streamType + ' event sent');
      }
    }
  }

  #playerStart() {
    this.#rmp.off('playing adstarted', this.#playerStartFn);
    if (this.#hasGtag()) {
      window.gtag('event', 'playerstart', this.#rmp.gaEventParameters);
      Logger.print('google-analytics', 'playerstart event sent');
    }
  }

  #srcChanged() {
    this.#rmp.off('playing adstarted', this.#playerStartFn);
    this.#rmp.on('playing adstarted', this.#playerStartFn);
  }

  #error() {
    if (this.#hasGtag()) {
      window.gtag('event', 'error', this.#rmp.gaEventParameters);
      Logger.print('google-analytics', 'error event sent');
    }
  }

  #pause() {
    if (this.#hasGtag()) {
      this.#rmp.one('play', () => {
        window.gtag('event', 'resume', this.#rmp.gaEventParameters);
        Logger.print('google-analytics', 'resume event sent');
      });
      window.gtag('event', 'pause', this.#rmp.gaEventParameters);
      Logger.print('google-analytics', 'pause event sent');
    }
  }

  #adImpression() {
    if (this.#hasGtag()) {
      window.gtag('event', 'adimpression', this.#rmp.gaEventParameters);
      Logger.print('google-analytics', 'adimpression event sent');
    }
  }

  #adError() {
    if (this.#hasGtag()) {
      let eventName = 'adloaderror';
      if (this.#rmp.adErrorType === 'adPlayError') {
        eventName = 'adplayerror';
      }
      window.gtag('event', eventName, this.#rmp.gaEventParameters);
      Logger.print('google-analytics', eventName + ' event sent');
    }
  }

  #adClick() {
    if (this.#hasGtag()) {
      window.gtag('event', 'adclick', this.#rmp.gaEventParameters);
      Logger.print('google-analytics', 'adclick event sent');
    }
  }

  #bufferNotStalledAnymore() {
    this.#rmp.off('playing canplay canplaythrough', this.#bufferNotStalledAnymoreFn);
    if (this.#hasGtag()) {
      window.gtag('event', 'buffernotstalledanymore', this.#rmp.gaEventParameters);
      Logger.print('google-analytics', 'buffernotstalledanymore event sent');
    }
  }

  #bufferStalled() {
    if (this.#hasGtag()) {
      this.#rmp.on('playing canplay canplaythrough', this.#bufferNotStalledAnymoreFn);
      window.gtag('event', 'bufferstalled', this.#rmp.gaEventParameters);
      Logger.print('google-analytics', 'bufferstalled event sent');
    }
  }

  #enterFullscreen() {
    if (this.#hasGtag()) {
      window.gtag('event', 'enterfullscreen', this.#rmp.gaEventParameters);
      Logger.print('google-analytics', 'enterfullscreen event sent');
    }
  }

  #exitFullscreen() {
    if (this.#hasGtag()) {
      window.gtag('event', 'exitfullscreen', this.#rmp.gaEventParameters);
      Logger.print('google-analytics', 'exitfullscreen event sent');
    }
  }

  #ended() {
    if (this.#hasGtag()) {
      window.gtag('event', 'ended', this.#rmp.gaEventParameters);
      Logger.print('google-analytics', 'ended event sent');
    }
  }

  #adParserLoadError() {
    if (this.#hasGtag()) {
      window.gtag('event', 'adblock', this.#rmp.gaEventParameters);
      Logger.print('google-analytics', 'adblock event sent');
    }
  }

  #seeking() {
    if (this.#hasGtag()) {
      if (this.#rmp.isVod) {
        window.gtag('event', 'seeking', this.#rmp.gaEventParameters);
        Logger.print('google-analytics', 'seeking event sent');
      }
    }
  }

  destroy() {
    window.clearInterval(this.#rmp.analyticsInterval);
    window.clearInterval(this.#fullMinuteWatchedInterval);
    if (this.#rmp.dom.container) {
      this.#rmp.off('ready', this.#readyFn);
      this.#rmp.off('pause', this.#gaPauseFn);
      this.#rmp.off('playing adstarted', this.#playerStartFn);
      this.#rmp.off('srcchanged', this.#srcChangedFn);
      this.#rmp.off('error', this.#errorFn);
      this.#rmp.off('adimpression', this.#adImpressionFn);
      this.#rmp.off('aderror', this.#adErrorFn);
      this.#rmp.off('adclick', this.#adClickFn);
      this.#rmp.off('enterfullscreen', this.#enterFullscreenFn);
      this.#rmp.off('exitfullscreen', this.#exitFullscreenFn);
      this.#rmp.off('ended', this.#gaEndedFn);
      this.#rmp.off('seeking', this.#seekingFn);
      this.#rmp.off('waiting', this.#bufferStalledFn);
      this.#rmp.off('playing canplay canplaythrough', this.#bufferNotStalledAnymoreFn);
      this.#rmp.off('adblock', this.#adParserLoadErrorFn);
    }
  }

  init() {
    Logger.print('info', 'init analytics');
    if (this.#rmp.gaEvents.length === 0) {
      this.#rmp.gaEvents = [
        'ready',
        'playerstart',
        'bufferstalled',
        'ended',
        'error',
        'adimpression',
        'adblock',
        'adplayerror',
        'adloaderror'
      ];
    }

    const possibleValues = [
      'context',
      'ready',
      'pause',
      'playerstart',
      'error',
      'bufferstalled',
      'fullscreen',
      'ended',
      'seeking',
      'fullminutewatched',
      'adimpression',
      'adplayerror',
      'adloaderror',
      'adclick'
    ];
    const sessionEvents = [];
    this.#rmp.gaEvents.forEach((event) => {
      if (possibleValues.indexOf(event) > -1) {
        sessionEvents.push(event);
      }
    });

    Logger.print('google-analytics', 'filtered sessionEvents follow', sessionEvents);
    sessionEvents.forEach((event) => {
      switch (event) {
        case 'context':
          this.#gaContext = true;
          break;
        case 'ready':
          this.#readyFn = this.#ready.bind(this);
          this.#rmp.one('ready', this.#readyFn);
          break;
        case 'playerstart':
          this.#playerStartFn = this.#playerStart.bind(this);
          this.#rmp.on('playing', this.#playerStartFn);
          if (this.#rmp.ads) {
            this.#rmp.on('adstarted', this.#playerStartFn);
          }
          this.#srcChangedFn = this.#srcChanged.bind(this);
          this.#rmp.on('srcchanged', this.#srcChangedFn);
          break;
        case 'error':
          this.#errorFn = this.#error.bind(this);
          this.#rmp.on('error', this.#errorFn);
          break;
        case 'pause':
          this.#gaPauseFn = this.#pause.bind(this);
          this.#rmp.on('pause', this.#gaPauseFn);
          break;
        case 'bufferstalled':
          this.#bufferStalledFn = this.#bufferStalled.bind(this);
          this.#bufferNotStalledAnymoreFn = this.#bufferNotStalledAnymore.bind(this);
          this.#rmp.on('waiting', this.#bufferStalledFn);
          break;
        case 'fullscreen':
          this.#enterFullscreenFn = this.#enterFullscreen.bind(this);
          this.#rmp.on('enterfullscreen', this.#enterFullscreenFn);
          this.#exitFullscreenFn = this.#exitFullscreen.bind(this);
          this.#rmp.on('exitfullscreen', this.#exitFullscreenFn);
          break;
        case 'ended':
          if (this.#rmp.isVod) {
            this.#gaEndedFn = this.#ended.bind(this);
            this.#rmp.on('ended', this.#gaEndedFn);
          }
          break;
        case 'seeking':
          if (this.#rmp.isVod) {
            this.#seekingFn = this.#seeking.bind(this);
            this.#rmp.on('seeking', this.#seekingFn);
          }
          break;
        case 'fullminutewatched':
          this.#fullMinuteWatchedInterval = window.setInterval(() => {
            if (this.#rmp.paused || this.#rmp.linearAdOnStage) {
              return;
            }
            if (this.#hasGtag()) {
              this.#fullMinuteWatchedTicker += 100;
              if (this.#fullMinuteWatchedTicker >= 60000) {
                window.gtag('event', 'fullminutewatched', this.#rmp.gaEventParameters);
                Logger.print('google-analytics', 'fullminutewatched event sent');
                this.#fullMinuteWatchedTicker = 0;
              }
            }
          }, 100);
          break;
        case 'adimpression':
          if (this.#rmp.ads) {
            this.#adImpressionFn = this.#adImpression.bind(this);
            this.#rmp.on('adimpression', this.#adImpressionFn);
          }
          break;
        case 'adplayerror':
          if (this.#rmp.ads) {
            this.#rmp.off('aderror', this.#adErrorFn);
            this.#adErrorFn = this.#adError.bind(this);
            this.#rmp.on('aderror', this.#adErrorFn);
          }
          break;
        case 'adloaderror':
          if (this.#rmp.ads) {
            this.#rmp.off('aderror', this.#adErrorFn);
            this.#adErrorFn = this.#adError.bind(this);
            this.#rmp.on('aderror', this.#adErrorFn);
          }
          break;
        case 'adclick':
          if (this.#rmp.ads) {
            this.#adClickFn = this.#adClick.bind(this);
            this.#rmp.on('adclick', this.#adClickFn);
          }
          break;
        case 'adblock':
          if (this.#rmp.ads) {
            this.#adParserLoadErrorFn = this.#adParserLoadError.bind(this);
            this.#rmp.on('adblock', this.#adParserLoadErrorFn);
          }
          break;
        default:
          break;
      }
    });
  }
}
